/**
 * Purchase Order Cancellation Hook
 * 
 * Manages the complete lifecycle of purchase order cancellation requests,
 * from initial client request through vendor response to final resolution.
 * This hook handles the complex workflow including cancellation fees,
 * disputes, and automated payment processing.
 * 
 * **Cancellation Workflow:**
 * 
 * 1. **Client Request** - Client initiates cancellation with reason
 * 2. **Vendor Response** - Vendor can:
 *    - Accept with no fee
 *    - Propose cancellation fee with justification
 * 3. **Client Decision** - Client reviews vendor response:
 *    - Accept fee and proceed with cancellation
 *    - Dispute fee and escalate to admin review
 * 4. **Resolution** - Automated or admin-mediated resolution
 * 
 * **Cancellation Fee Handling:**
 * - Vendor calculates fee based on work completed
 * - Supporting documentation for fee justification
 * - Fee charged to client if accepted
 * - Dispute escalation if client disagrees
 * 
 * **Timeout Protection:**
 * - Vendor has limited time to respond
 * - Auto-approval after timeout period
 * - Prevents indefinite pending states
 * 
 * **Available Mutations:**
 * - `requestCancellation` - Client initiates cancellation
 * - `submitVendorResponse` - Vendor responds with fee/no-fee
 * - `submitClientApproval` - Client accepts or disputes fee
 * 
 * @returns {Object} PO cancellation mutations
 * @returns {Function} requestCancellation - Initiate cancellation request
 * @returns {Function} submitVendorResponse - Vendor responds to request
 * @returns {Function} submitClientApproval - Client accepts/disputes fee
 * 
 * @example
 * // Client requests cancellation
 * const { requestCancellation } = usePOCancellation();
 * 
 * requestCancellation.mutate({
 *   purchaseOrderId: 'po-123',
 *   reason: 'Project requirements changed'
 * });
 * 
 * @example
 * // Vendor responds with cancellation fee
 * const { submitVendorResponse } = usePOCancellation();
 * 
 * submitVendorResponse.mutate({
 *   cancellationRequestId: 'req-456',
 *   cancellationChargeCents: 50000, // $500.00
 *   feeExplanation: 'Materials already ordered',
 *   supportingDocuments: [{ url: '...', type: 'invoice' }]
 * });
 * 
 * @example
 * // Client accepts or disputes fee
 * const { submitClientApproval } = usePOCancellation();
 * 
 * submitClientApproval.mutate({
 *   cancellationRequestId: 'req-456',
 *   decision: 'accept' // or 'dispute'
 * });
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { toast } from 'sonner';
import { CancellationDocument } from '@/lib/types/cancellation';

interface RequestCancellationParams {
  purchaseOrderId: string;
  reason: string;
}

interface VendorResponseParams {
  cancellationRequestId: string;
  acceptWithNoFee?: boolean;
  cancellationChargeCents?: number;
  feeExplanation?: string;
  supportingDocuments?: CancellationDocument[];
}

interface ClientApprovalParams {
  cancellationRequestId: string;
  decision: 'accept' | 'dispute';
  disputeReason?: string;
  disputeDetails?: string;
}

export const usePOCancellation = () => {
  const queryClient = useQueryClient();

  const requestCancellation = useMutation({
    mutationFn: async (params: RequestCancellationParams) => {
      const { data, error } = await invokeEdgeFunction('request-po-cancellation', {
        body: params,
        callerOperation: 'usePOCancellation.requestCancellation',
      });

      if (error) throw error;
      return data as { message?: string };
    },
    onSuccess: (data, variables) => {
      toast.success(data?.message || 'Cancellation request submitted');
      queryClient.invalidateQueries({ queryKey: ['purchase-order', variables.purchaseOrderId] });
      queryClient.invalidateQueries({ queryKey: ['po-modification-requests', variables.purchaseOrderId] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to submit cancellation request');
    },
  });

  const submitVendorResponse = useMutation({
    mutationFn: async (params: VendorResponseParams) => {
      const { data, error } = await invokeEdgeFunction('submit-vendor-cancellation-response', {
        body: params,
        callerOperation: 'usePOCancellation.submitVendorResponse',
      });

      if (error) throw error;
      return data as { message?: string };
    },
    onSuccess: (data, variables) => {
      toast.success(data?.message || 'Response submitted');
      
      // Invalidate all relevant queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['po-modification-requests'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-order'] });
      
      // If we can extract the purchase order ID from the cancellation request, invalidate that specific query
      const cancellationRequestId = variables.cancellationRequestId;
      if (cancellationRequestId) {
        queryClient.invalidateQueries({ queryKey: ['po-modification-requests', cancellationRequestId] });
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to submit response');
    },
  });

  const submitClientApproval = useMutation({
    mutationFn: async (params: ClientApprovalParams) => {
      const { data, error } = await invokeEdgeFunction('client-approve-cancellation-fee', {
        body: params,
        callerOperation: 'usePOCancellation.submitClientApproval',
      });

      if (error) throw error;
      return data as { message?: string };
    },
    onSuccess: (data) => {
      toast.success(data?.message || 'Decision submitted');
      queryClient.invalidateQueries({ queryKey: ['po-modification-requests'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-order'] });
      queryClient.invalidateQueries({ queryKey: ['job-reconciliation'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation-adjustments'] });
      queryClient.invalidateQueries({ queryKey: ['disputes'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to submit decision');
    },
  });

  return {
    requestCancellation,
    submitVendorResponse,
    submitClientApproval,
  };
};
